<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import Popper from 'vue3-popper'
import dayjs from 'dayjs'
import {
  RefreshCw05Icon,
  Users01Icon,
  Dataflow01Icon,
  Loading02Icon
} from 'front-library'
import { getProcessRequests } from '@/app/core/services/trafficlight'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'

const store = useUserStore()
const router = useRouter()

const loading = ref(false)
const amountProcessComplete = ref()
const amountProcessProgress = ref()
const amountProcessError = ref()
const dataRequestsProcess = ref([] as any[])
const interval = ref()

const totalProcesses = ref(0)
const countOldNotification = ref(0)

/* Contar la cantidad de procesos por estado */
const countRequestsProcess = () => {
  amountProcessComplete.value = 0
  amountProcessProgress.value = 0
  amountProcessError.value = 0
  dataRequestsProcess.value.forEach(_ => {
    if (_.status === 'COMPLETE') amountProcessComplete.value++
    else if (
      _.status === 'IN_PROGRESS' ||
      _.status === 'NO_STARTED' ||
      _.status === 'PENDING'
    )
      amountProcessProgress.value++
    else if (_.status === 'ERROR') amountProcessError.value++
  })
}

const refreshProcesses = async () => {
  dataRequestsProcess.value = await getProcessRequests()
  countRequestsProcess()
  trafficLight()
}

const refreshWithLoading = async () => {
  loading.value = true
  await refreshProcesses()
  loading.value = false
}

const openProcesses = () => {
  trafficLight()
  store.notification_trafficLight = 0
  totalProcesses.value = dataRequestsProcess.value.length
  const meters = document.querySelectorAll('svg.progress .meter')
  if (meters.length > 0) {
    meters.forEach((path: any) => {
      path.style.strokeDashoffset = 282.78314208984375
      const length = path.getTotalLength()
      const value = parseInt(path.parentNode.getAttribute('data-value'))
      const to = length * ((100 - value) / 100)
      path.getBoundingClientRect()
      setTimeout(() => {
        path.style.strokeDashoffset = Math.max(0, to)
        //
        path.classList.add('progress')
        path.nextElementSibling.textContent = `${value}%`
      }, 100)
    })
  }
}

const trafficLight = () => {
  const path: any = document.querySelector('svg.trafficLight .meter')
  const length = path.getTotalLength()
  let processLength = 0
  countRequestsProcess()
  if (amountProcessError.value) {
    path.classList.add('error')
    path.classList.remove('progress')
    path.classList.remove('complete')
    processLength = amountProcessError.value
  } else if (amountProcessProgress.value) {
    path.classList.add('progress')
    path.classList.remove('error')
    path.classList.remove('complete')
    processLength = amountProcessProgress.value
  } else if (amountProcessComplete.value) {
    path.classList.add('complete')
    path.classList.remove('progress')
    path.classList.remove('error')
    processLength = amountProcessComplete.value
  }

  if (
    totalProcesses.value !== 0 &&
    dataRequestsProcess.value.length > totalProcesses.value
  ) {
    const newCountNotification =
      dataRequestsProcess.value.length - totalProcesses.value
    if (newCountNotification !== countOldNotification.value) {
      const notification = document.getElementById('notification-bounce')
      notification?.classList.add('bounce')
      setTimeout(() => {
        notification?.classList.remove('bounce')
      }, 4000)
      store.notification_trafficLight = newCountNotification
      countOldNotification.value = newCountNotification
    }
  }

  const value = (processLength * 100) / dataRequestsProcess.value.length
  const to = length * ((100 - value) / 100)
  path.getBoundingClientRect()
  path.style.strokeDashoffset = Math.max(0, to)
  path.nextElementSibling.textContent = `${processLength ?? 0}/${
    dataRequestsProcess.value.length
  }`
}

const goToPayrollView = async (
  processedPayrollId: string,
  idPendingProcesses: string
) => {
  const type: string = 'premovements'
  await router.push({
    name: 'DetailPayrollSheet',
    params: { id: processedPayrollId, type, idPendingProcesses }
  })
}

onMounted(async () => {
  await refreshProcesses()
  totalProcesses.value = dataRequestsProcess.value.length
  // dataRequestsProcess.value = await getProcessRequests()
  // trafficLight()
  interval.value = setInterval(async () => {
    store.tokenInvalid ? clearInterval(interval.value) : ''
    await refreshProcesses().catch(e => {
      console.error(e)
    })
  }, 5000)
})

onBeforeUnmount(() => {
  clearInterval(interval.value)
})
</script>
<template>
  <div
    class="pr-5 sm:pt-1 lg:pt-4 justify-end flex lg:fixed right-0"
    style="z-index: 32">
    <Popper
      arrow
      offsetDistance="14"
      :hover="false"
      placement="bottom"
      class="process-trafficLight">
      <div class="cursor-pointer" @click="openProcesses">
        <svg
          class="trafficLight cursor-pointer"
          viewBox="0 0 100 100"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none">
          <circle cx="50" cy="50" r="37" />
          <path
            class="meter"
            d="M 50,50 
           m -37, 0
           a 37,37 0 1,0 74,0
           a 37,37 0 1,0 -74,0"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-dashoffset="232.510498046875"
            stroke-dasharray="232.510498046875" />
          <text
            x="50"
            y="50"
            text-anchor="middle"
            dominant-baseline="central"
            font-size="13"></text>
        </svg>
      </div>
      <template #content>
        <div class="box-process">
          <div class="absolute top-6 right-6">
            <RefreshCw05Icon
              @click="refreshWithLoading()"
              class="cursor-pointer text-gray-400" />
          </div>
          <div
            v-if="!loading"
            class="flex justify-center items-center gap-3 mb-4">
            <div
              class="flex items-center justify-center rounded-full bg-green-100 h-11 w-11">
              <p class="text-green-800 font-semibold">
                {{ amountProcessComplete }}
              </p>
            </div>
            <div
              class="flex items-center justify-center rounded-full bg-blue-100 h-11 w-11">
              <p class="text-blue-800 font-semibold">
                {{ amountProcessProgress }}
              </p>
            </div>
            <div
              class="flex items-center justify-center rounded-full bg-red-100 h-11 w-11">
              <p class="text-red-800 font-semibold">{{ amountProcessError }}</p>
            </div>
          </div>
          <div v-if="!loading" class="overflow-auto scroll h-list">
            <div
              v-for="(process, index) in dataRequestsProcess"
              :key="index"
              class="flex justify-center items-center shadow-lg rounded-3xl mb-3">
              <div
                class="px-2.5 h-28 w-12 flex justify-center items-center rounded-s-3xl"
                :style="`background-color:${process.process_color}`">
                <p class="text-white font-medium text-4xl">
                  {{ process.process }}
                </p>
              </div>
              <div class="text-center w-60">
                <p class="font-semibold leading-4">{{ process.payroll }}</p>
                <p>{{ process.start_date + ' - ' + process.end_date }}</p>
                <div class="flex justify-center gap-2 py-0.5">
                  <div class="flex gap-1">
                    <Users01Icon class="text-gray-400 h-5 w-5" />
                    <p class="font-semibold">{{ process.employees_amount }}</p>
                  </div>
                  <div class="flex gap-1">
                    <Dataflow01Icon class="text-gray-400 h-5 w-5" />
                    <p class="font-semibold">{{ process.concepts_amount }}</p>
                  </div>
                </div>
                <p class="leading-3 px-4 text-gray-400" style="font-size: 12px">
                  <!-- Lorem ipsum dolor sit amet, consectetur adipiscing elit. -->
                </p>
              </div>
              <div class="pr-2.5 relative">
                <div>
                  <svg
                    v-if="
                      process.status == 'PENDING' || process.status == 'NOSTART'
                    "
                    class="progress mb-3"
                    viewBox="0 0 100 100"
                    xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="none"
                    :data-value="process.progress ?? 0">
                    <circle r="45" cx="50" cy="50" />
                    <path
                      class="meter"
                      :style="`stroke:${process.process_color}`"
                      d="M5,50a45,45 0 1,0 90,0a45,45 0 1,0 -90,0"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-dashoffset="282.78314208984375"
                      stroke-dasharray="282.78314208984375" />
                    <text
                      x="50"
                      y="50"
                      text-anchor="middle"
                      dominant-baseline="central"
                      font-size="20"></text>
                  </svg>
                  <svg
                    @click="
                      goToPayrollView(process.processed_payroll_id, process.id)
                    "
                    v-else-if="process.status == 'COMPLETE'"
                    class="checkmark cursor-pointer"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 52 52">
                    <circle
                      class="checkmark_circle"
                      cx="26"
                      cy="26"
                      r="25"
                      fill="none" />
                    <path
                      class="checkmark_check"
                      fill="none"
                      d="M 19 29 l 5 4 l 10 -11" />
                  </svg>
                  <svg
                    v-else-if="process.status == 'ERROR'"
                    class="errormark"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 52 52">
                    <circle
                      class="errormark_circle"
                      cx="26"
                      cy="26"
                      r="25"
                      fill="none" />
                    <path
                      class="errormark_check"
                      fill="none"
                      d="M 20.5 20 l 12 13 m 0 -13 l -12 13" />
                  </svg>
                </div>
                <div class="absolute time-card">
                  <p class="text-xxs">
                    hace
                    {{
                      dayjs().diff(process.created_at, 's') < 60
                        ? ' 1 min'
                        : dayjs().diff(process.created_at, 'm') < 60
                        ? dayjs().diff(process.created_at, 'm') + ' min'
                        : dayjs().diff(process.created_at, 'h') < 24
                        ? dayjs().diff(process.created_at, 'h') + ' hrs'
                        : dayjs().diff(process.created_at, 'd') <= 1
                        ? dayjs().diff(process.created_at, 'd') + ' día'
                        : dayjs().diff(process.created_at, 'd') + ' días'
                    }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="h-list flex justify-center items-center">
            <Loading02Icon class="rotate h-12 w-12 text-gray-400" />
          </div>
        </div>
      </template>
    </Popper>
    <div
      id="notification-bounce"
      class="absolute justify-center items-center rounded-full h-4 w-4 bg-red-500 text-white notification bounce"
      :class="[store.notification_trafficLight > 0 ? 'flex' : 'hidden']">
      <p>{{ store.notification_trafficLight }}</p>
    </div>
  </div>
</template>
<style scoped>
/* semaforo */
svg.trafficLight {
  width: 65px;
  height: 65px;
  filter: drop-shadow(1px 2px 4px rgb(0 0 0 / 0.17));
}
svg.progress,
svg.status {
  width: 60px;
  height: 60px;
  filter: drop-shadow(1px 2px 4px rgb(0 0 0 / 0.17));
}
svg.trafficLight circle {
  stroke: #ffffff;
  stroke-width: 25px;
  stroke-dasharray: 0;
  fill: none;
}
svg.trafficLight .meter {
  stroke-width: 25px;
  fill: none;
  transition: stroke-dashoffset 1s cubic-bezier(0.43, 0.41, 0.22, 0.91);
  transform-origin: center center;
  transform: rotate(-90deg) scaleX(-1);
}
svg.trafficLight .meter.complete {
  stroke: #12b76a;
}
svg.trafficLight .meter.progress {
  stroke: #1770ef;
}
svg.trafficLight .meter.error {
  stroke: #e15252;
}
svg.trafficLight text {
  fill: #101929;
  font-weight: bold;
}
/* box */
.process-trafficLight {
  --popper-theme-background-color: #ffffff;
  --popper-theme-background-color-hover: #ffffff;
  --popper-theme-text-color: #666f85;
  --popper-theme-border-radius: 20px;
  --popper-theme-padding: 20px 20px;
  --popper-theme-box-shadow: -1px 2px 13px -4px rgba(0, 0, 0, 0.25);
  font-size: 14px;
}
:deep(.popper) {
  margin-left: -20px !important;
}
:deep(.popper #arrow) {
  left: 18px !important;
}
.box-process {
  width: 360px;
  height: calc(100vh - 160px);
}
/* progress process */
svg.progress circle {
  stroke-width: 10px;
  stroke-dasharray: 0;
  stroke: #ffffff;
  fill: none;
}
svg.progress .meter {
  stroke-width: 10px;
  fill: none;
  transition: stroke-dashoffset 1s cubic-bezier(0.43, 0.41, 0.22, 0.91);
  transform-origin: center center;
  transform: rotate(-90deg) scaleX(-1);
}
svg.progress text {
  fill: #5f5f5fd6;
  font-weight: 600;
  font-size: 22px;
}
.text-xxs {
  font-size: 10px;
}
.time-card {
  bottom: -13px;
  right: 15px;
  left: -4px;
}
.h-list {
  height: calc(100vh - 224px);
}
/* process check or error */
.errormark,
.checkmark {
  width: 60px;
  height: 60px;
  margin-bottom: 12px;
  border-radius: 50%;
  stroke-width: 2.5px;
  stroke: #fff;
  stroke-miterlimit: 10;
}
.errormark {
  box-shadow: inset 0px 0px 0px rgb(254, 226, 226);
  animation: fill-error 0.4s ease-in-out 0.4s forwards,
    scale 0.3s ease-in-out 0.9s both;
}
.checkmark {
  box-shadow: inset 0px 0px 0px rgb(220, 252, 231);
  animation: fill-check 0.4s ease-in-out 0.4s forwards,
    scale 0.3s ease-in-out 0.9s both;
}
.errormark_circle,
.checkmark_circle {
  stroke-dasharray: 166;
  stroke-dashoffset: 166;
  stroke-width: 2;
  stroke-miterlimit: 10;
  fill: none;
  animation: stroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards;
}
.errormark_circle {
  stroke: rgb(254, 226, 226);
}
.checkmark_circle {
  stroke: rgb(220, 252, 231);
}
.errormark_check,
.checkmark_check {
  transform-origin: 50% 50%;
  stroke-dasharray: 48;
  stroke-dashoffset: 48;
  stroke-linecap: round;
  animation: stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.8s forwards;
}
.errormark_check {
  stroke: rgb(153, 27, 27);
}
.checkmark_check {
  stroke: rgb(22, 101, 52);
}

@keyframes stroke {
  100% {
    stroke-dashoffset: 0;
  }
}
@keyframes scale {
  0%,
  100% {
    transform: none;
  }
  50% {
    transform: scale3d(1.1, 1.1, 1);
  }
}
@keyframes fill-check {
  100% {
    box-shadow: inset 0px 0px 0px 33px rgb(220, 252, 231);
  }
}
@keyframes fill-error {
  100% {
    box-shadow: inset 0px 0px 0px 33px rgb(254, 226, 226);
  }
}
/* notification */
.notification {
  font-size: 9px;
}
.notification.bounce {
  -webkit-animation-name: bounce;
  animation-name: bounce;
  -webkit-animation-duration: 1s;
  animation-duration: 1s;
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-8px);
  }
  60% {
    transform: translateY(-7px);
  }
}
</style>
